import React from 'react';
import { CheckCircle2, Clock, Info, ListChecks, Loader2, Receipt, X } from 'lucide-react';
import { folgeZeilen, istHinweisZeile, type FolgeZeile } from './abschlussLogic';
import type { AbschlussStand } from './useBestandteilAbschluss';

/**
 * Die Meldung des Abschlussschritts zu den Bestandteilen — in allen sechs
 * Modulen dieselbe (Plan B5, Faelle 2 und 3):
 *
 *   ✔ Bestandteil „Vorankündigung“ der Position „Planungsphase“ erledigt (4/5)
 *   ⏱ Stunden erfasst: 8 h        (bzw. gelb „Stunden fehlen — …“, S3)
 *   ✔ Position „Planungsphase“ gebucht — jetzt abrechenbar
 *   ℹ Hinweise (offen gebliebene Bestandteile, Lesefehler, Nur-Ansicht …)
 *
 * Nur Anzeige: was gemeldet wird, entscheiden `abschlussLogic.folgeZeilen`
 * und der Hook. KEINE PREISE — die Meldung nennt Bestandteile und Positionen,
 * nie Geld (Kernregel 6). Ohne etwas zu melden rendert sie nichts.
 *
 * Das Stunden-Pflichtfenster zeichnet sie NIE: manche Module rendern diese
 * Meldung zweimal (Vorankuendigung im Exportdialog und darunter,
 * Baustellenordnung im Abschlussdialog und ueber den Reitern) — das Fenster
 * steht je Modul genau einmal (`BestandteilStundenPflicht`). Hier stehen nur
 * die Zeilen zu den Stunden, sobald das Fenster zu ist.
 */

const ICON: Record<FolgeZeile['art'], React.ReactNode> = {
    erledigt: <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />,
    gebucht: <Receipt className="w-3.5 h-3.5 text-emerald-700 shrink-0 mt-0.5" />,
    hinweis: <Info className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />,
    stunden: <Clock className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />,
    stunden_fehlen: <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />,
};

const TEXT_KLASSE: Record<FolgeZeile['art'], string> = {
    erledigt: 'text-emerald-900',
    gebucht: 'text-emerald-900 font-bold',
    hinweis: 'text-amber-900',
    stunden: 'text-emerald-900',
    stunden_fehlen: 'text-amber-900 font-bold',
};

export const BestandteilAbschlussHinweis: React.FC<{
    stand: AbschlussStand | null | undefined;
    /** „Ausblenden" — ohne Rueckruf kein Knopf. */
    onSchliessen?: () => void;
    className?: string;
}> = ({ stand, onSchliessen, className = '' }) => {
    if (!stand) return null;
    if (stand.laeuft) {
        return (
            <div className={`text-xs border rounded-lg px-3 py-2 flex items-start gap-2 bg-slate-50 border-slate-200 text-slate-700 print:hidden ${className}`}>
                <Loader2 className="w-3.5 h-3.5 shrink-0 mt-0.5 animate-spin" />
                <span>Bestandteile der Paketposition werden geprüft…</span>
            </div>
        );
    }
    const zeilen = folgeZeilen(stand.folge, stand.zusatz, stand.stunden);
    if (zeilen.length === 0) return null;
    const nurHinweise = zeilen.every(istHinweisZeile);
    const rahmen = nurHinweise ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200';
    return (
        <div className={`text-xs border rounded-lg px-3 py-2 flex items-start gap-2 print:hidden ${rahmen} ${className}`} role="status">
            <ListChecks className={`w-4 h-4 shrink-0 mt-px ${nurHinweise ? 'text-amber-600' : 'text-emerald-700'}`} />
            <div className="flex-1 min-w-0">
                <p className={`font-bold mb-1 ${nurHinweise ? 'text-amber-900' : 'text-emerald-900'}`}>Bestandteile</p>
                <ul className="space-y-1">
                    {zeilen.map((z, i) => (
                        <li key={i} className="flex items-start gap-1.5 leading-snug">
                            {ICON[z.art]}
                            <span className={`min-w-0 break-words ${TEXT_KLASSE[z.art]}`}>{z.text}</span>
                        </li>
                    ))}
                </ul>
            </div>
            {onSchliessen && (
                <button
                    type="button"
                    onClick={onSchliessen}
                    title="Hinweis ausblenden"
                    className="shrink-0 p-0.5 rounded hover:bg-black/5 transition"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            )}
        </div>
    );
};
